/**
 * Error Manager
 * Manages error display and error containers
 */

import { FormatUtils } from '../utils/format_utils.js';

export class ErrorManager {
  /**
   * Ensure error container exists for field
   * @param {Element} field - Field element
   * @returns {Element|null}
   */
  static ensureErrorContainer(field) {
    const fieldContainer = field.closest('.form-group') || field.parentNode;
    if (!fieldContainer) return null;

    // Check for existing error container
    let errorContainer = fieldContainer.querySelector('.error-container');
    
    // Also check for traditional error_message and remove it to prevent duplicates
    const fieldName = field.name || field.id;
    const traditionalError = document.getElementById(`err_${fieldName}`);
    if (traditionalError) {
      traditionalError.remove(); // Remove to prevent duplicates
    }
    
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.className = 'error-container';
      errorContainer.setAttribute('data-permanent', 'true');
      errorContainer.setAttribute('data-field-id', fieldName);
      
      // Insert after field or nice-select
      if (field.tagName === 'SELECT') {
        const niceSelectDiv = fieldContainer.querySelector('.nice-select');
        if (niceSelectDiv && niceSelectDiv.nextSibling) {
          fieldContainer.insertBefore(errorContainer, niceSelectDiv.nextSibling);
        } else if (niceSelectDiv) {
          fieldContainer.appendChild(errorContainer);
        } else {
          if (field.nextSibling) {
            fieldContainer.insertBefore(errorContainer, field.nextSibling);
          } else {
            fieldContainer.appendChild(errorContainer);
          }
        }
      } else {
        if (field.nextSibling) {
          fieldContainer.insertBefore(errorContainer, field.nextSibling);
        } else {
          fieldContainer.appendChild(errorContainer);
        }
      }
    }
    
    return errorContainer;
  }

  /**
   * Show error for field
   * @param {Element} field - Field element
   * @param {string} errorMessage - Error message
   */
  static showError(field, errorMessage) {
    if (!field) return;
    
    const errorContainer = this.ensureErrorContainer(field);
    if (errorContainer) {
      const clippedMessage = FormatUtils.clipText(errorMessage, 45);
      errorContainer.textContent = clippedMessage;
      errorContainer.classList.add('validation-error-message');
    }
    
    // Hide traditional error elements to prevent duplicates
    const fieldName = field.name || field.id;
    const traditionalError = document.getElementById(`err_${fieldName}`);
    if (traditionalError) {
      traditionalError.classList.add('d-none');
      traditionalError.textContent = ''; // Clear to prevent display
    }
  }

  /**
   * Clear error for field
   * @param {Element} field - Field element
   */
  static clearError(field) {
    if (!field) return;
    
    const fieldContainer = field.closest('.form-group') || field.parentNode;
    if (fieldContainer) {
      const errorContainer = fieldContainer.querySelector('.error-container');
      if (errorContainer) {
        errorContainer.textContent = '';
        errorContainer.classList.remove('validation-error-message');
      }
    }
    
    // Also clear traditional error
    const fieldName = field.name || field.id;
    const traditionalError = document.getElementById(`err_${fieldName}`);
    if (traditionalError) {
      traditionalError.classList.add('d-none');
      traditionalError.textContent = ''; // Clear content to prevent display
    }
  }

  /**
   * Clear all errors
   */
  static clearAllErrors() {
    // Clear error containers
    const errorContainers = document.querySelectorAll('.error-container.validation-error-message');
    errorContainers.forEach(container => {
      container.textContent = '';
      container.classList.remove('validation-error-message');
    });
    
    // Clear traditional errors
    const traditionalErrors = document.querySelectorAll('.error_message.validation-error-message:not(.error-container)');
    traditionalErrors.forEach(error => {
      if (!error.id.startsWith('err_')) {
        error.classList.add('d-none');
      }
    });
  }

  /**
   * Check if field has error
   * @param {Element} field - Field element
   * @returns {boolean}
   */
  static hasError(field) {
    if (!field) return false;
    
    const fieldContainer = field.closest('.form-group') || field.parentNode;
    if (fieldContainer) {
      const errorContainer = fieldContainer.querySelector('.error-container.validation-error-message');
      if (errorContainer && errorContainer.textContent.trim()) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get error message for field
   * @param {Element} field - Field element
   * @returns {string}
   */
  static getError(field) {
    if (!field) return '';
    
    const fieldContainer = field.closest('.form-group') || field.parentNode;
    if (fieldContainer) {
      const errorContainer = fieldContainer.querySelector('.error-container.validation-error-message');
      if (errorContainer) {
        return errorContainer.textContent.trim();
      }
    }
    
    return '';
  }

  /**
   * Reposition error containers for select elements after nice-select initialization
   */
  static repositionSelectErrorContainers() {
    const selects = document.querySelectorAll('select[required]');
    selects.forEach(select => {
      const fieldContainer = select.closest('.form-group');
      if (!fieldContainer) return;

      const errorContainer = fieldContainer.querySelector('.error-container');
      const niceSelectDiv = fieldContainer.querySelector('.nice-select');

      if (errorContainer && niceSelectDiv) {
        // Remove and reinsert after nice-select
        errorContainer.remove();
        if (niceSelectDiv.nextSibling) {
          fieldContainer.insertBefore(errorContainer, niceSelectDiv.nextSibling);
        } else {
          fieldContainer.appendChild(errorContainer);
        }
      } else if (!errorContainer && niceSelectDiv && select.hasAttribute('required')) {
        // Create error container if it doesn't exist
        this.ensureErrorContainer(select);
      }
    });
  }
}

